import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  ApiCodeGenerationInitializeRequest,
  ApiCodeGenerationRequest,
} from 'sprites-types/api/codegen'

export type CodegenStatus =
  | 'none'
  | 'initializing'
  | 'generating'
  | 'deploying' // Deploying is during code generation.
  | 'redeploying' // Redeploying is just deploying an existing app or verifying the google cloud run container..

export interface WebsocketState {
  connected: boolean
  connecting: boolean
  connectionId?: string
  codegenId?: string
  reconnectAttempts: number
  isReconnecting: boolean
  error?: string
  agentId?: string
  profiling?: string | null
  codegenStatus: CodegenStatus
}

const initialState: WebsocketState = {
  connected: false,
  connecting: false,
  reconnectAttempts: 0,
  isReconnecting: false,
  codegenStatus: 'initializing',
}

export const copilotConnect = createAction<{ agentId: string; profiling?: string | null }>(
  'copilot/connect'
)
export const copilotDisconnect = createAction('copilot/disconnect')
export const copilotReconnect = createAction('copilot/reconnect')

export const copilotSendMessage = createAction<ApiCodeGenerationRequest>('copilot/sendMessage')
export const copilotSendInitialize =
  createAction<ApiCodeGenerationInitializeRequest>('copilot/sendInitialize')
export const copilotSubmitMessage = createAction<{
  message: string
  agentId: string
  appendLocally?: boolean
}>('copilot/submitMessage')
export const copilotAbortCodegen = createAction('copilot/abortCodegen')
export const copilotInitializeAgent = createAction<{ agentId: string }>('copilot/initializeAgent')

const copilotSlice = createSlice({
  name: 'copilot',
  initialState,
  reducers: {
    connect: (state, action: PayloadAction<{ agentId: string; profiling?: string | null }>) => {
      state.connecting = true
      state.agentId = action.payload.agentId
      state.profiling = action.payload.profiling
      state.error = undefined
    },
    connected: (state, action: PayloadAction<{ connectionId?: string }>) => {
      state.connected = true
      state.connecting = false
      state.connectionId = action.payload.connectionId
      state.reconnectAttempts = 0
      state.isReconnecting = false
      state.error = undefined
    },
    disconnect: (state) => {
      state.connected = false
      state.connecting = false
      state.connectionId = undefined
      state.codegenId = undefined
      state.codegenStatus = 'initializing'
    },
    connectionError: (state, action: PayloadAction<string>) => {
      state.connected = false
      state.connecting = false
      state.error = action.payload
    },
    startReconnecting: (state) => {
      state.isReconnecting = true
      state.reconnectAttempts += 1
    },
    resetReconnectAttempts: (state) => {
      state.reconnectAttempts = 0
      state.isReconnecting = false
    },
    setCodegenId: (state, action: PayloadAction<string>) => {
      state.codegenId = action.payload
    },
    clearCodegenId: (state) => {
      state.codegenId = undefined
    },
    setCodegenStatus: (state, action: PayloadAction<CodegenStatus>) => {
      state.codegenStatus = action.payload
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(copilotConnect, (state, action) => {
      state.connecting = true
      state.agentId = action.payload.agentId
      state.profiling = action.payload.profiling
      state.error = undefined
    })
  },
})

export const {
  connect,
  connected,
  disconnect,
  connectionError,
  startReconnecting,
  resetReconnectAttempts,
  setCodegenId,
  clearCodegenId,
  setCodegenStatus,
  reset,
} = copilotSlice.actions

export default copilotSlice.reducer
